import EqualizerIcon from '@mui/icons-material/Equalizer';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    useGetAllReservationsQuery,
    useGetIndividualOwnerAssetsQuery,
    useGetReportQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import DropdownFilter from '../../../../layout/styledcomponents/filters/Dropdown';
import ReportGraph from './ReportGraph';
import ReportList from './ReservationsReportList';
import moment from 'moment';
import { startOfYear } from 'date-fns';
import UserRole from '../../../../shared/AuthRoles';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import MultiSelectDropdownFilter from '../../../../layout/styledcomponents/filters/MultiSelectDropdown';
import GroupedExperienceTypeFilter, {
    buildGroupedExperiences,
} from '../../../../layout/styledcomponents/filters/GroupedExperienceTypeFilter';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { IconButton, InputAdornment, Popover } from '@mui/material';
import { Calendar } from 'react-date-range';
import { ReportType } from '../../../pages/Reports Page/IndividualReportPage';

const getCss = (theme: Theme) => `
    .rdrMonthAndYearPickers select {
        color: ${theme.palette.text.primary};
        font-size: 1rem;
        font-weight: 500;
    }

    .rdrDayNumber span {
        color: ${theme.palette.text.primary};
        font-weight: 500;
    }

    .rdrDayToday .rdrDayNumber span:after {
        background-color: ${theme.palette.secondary.main}
    }

    .rdrWeekDay {
        color: ${theme.palette.text.primary};
        font-weight: 600;
    }
`;

export default function ReportComponentPage({
    reportType,
}: {
    reportType: ReportType;
}) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role.includes('owner');
    const [fromDate, setFromDate] = useState(startOfYear(new Date()));
    const [toDate, setToDate] = useState(new Date());
    const [guestId, setGuestId] = useState({ value: null, label: 'All' });
    const [ownerId, setOwnerId] = useState({ value: null, label: 'All' });
    const [visitType, setVisitType] = useState('');
    const [bookerType, setBookerType] = useState('');
    const [moduleType, setModuleType] = useState([
        'boat',
        'property',
        'vehicle',
        'activity',
        'commercial',
    ]);
    const [status, setStatus] = useState<any>([
        'pending',
        'approved',
        'completed',
        'cancelled',
        'declined',
    ]);
    const [reportPeriod, setReportPeriod] = useState(
        reportType === ReportType.FY
            ? 'prev-fy'
            : reportType === ReportType.Monthly
              ? 'prev-month'
              : reportType === ReportType.Quarterly
                ? 'prev-qrt'
                : reportType === ReportType.YTD
                  ? 'ytd'
                  : '',
    );

    const defaultPeriod: Record<ReportType, string> = {
        [ReportType.FY]: 'prev-fy',
        [ReportType.Monthly]: 'prev-month',
        [ReportType.Quarterly]: 'prev-qrt',
        [ReportType.YTD]: 'ytd',
        [ReportType.Reservations]: '',
        [ReportType.Cancellations]: '',
        [ReportType.Declined]: '',
    };

    useEffect(() => {
        setReportPeriod(defaultPeriod[reportType]);
    }, [reportType]);

    const [openCalendar1, setOpenCalendar1] = useState(false);
    const [anchorElCalendar1, setAnchorElCalendar1] =
        React.useState<null | HTMLElement>(null);
    const [openCalendar2, setOpenCalendar2] = useState(false);
    const [anchorElCalendar2, setAnchorElCalendar2] =
        React.useState<null | HTMLElement>(null);
    const [companyGuestList, setGuestCompanyList] = useState<any>([]);
    const [companyOwnerList, setOwnerCompanyList] = useState<any>([]);

    const [showGraphView, setShowGraphView] = useState(false);

    const { data: ownerModules } = useGetIndividualOwnerAssetsQuery(
        {
            url: `owners/${loggedInUser?.company}/modules`,
            sort: 'name',
            status: 'published',
        },
        {
            skip:
                !isOwner ||
                reportType !== ReportType.Reservations ||
                !loggedInUser?.company,
        },
    );

    const ownerModuleList = useMemo(
        () => (Array.isArray(ownerModules) ? ownerModules : []),
        [ownerModules],
    );

    const moduleTypesForBookingsQuery = useMemo(() => {
        if (
            isOwner &&
            reportType === ReportType.Reservations &&
            ownerModuleList.length > 0
        ) {
            return Array.from(
                new Set(
                    ownerModuleList.map((m: { type: string }) =>
                        String(m.type).toLowerCase(),
                    ),
                ),
            );
        }
        return moduleType;
    }, [isOwner, reportType, ownerModuleList, moduleType]);

    const [selectedOwnerModuleIds, setSelectedOwnerModuleIds] = useState<
        number[]
    >([]);
    const [
        ownerModuleSelectionInitialized,
        setOwnerModuleSelectionInitialized,
    ] = useState(false);

    const ownerModuleSelectionDidInitRef = useRef(false);

    useEffect(() => {
        if (reportType !== ReportType.Reservations) {
            ownerModuleSelectionDidInitRef.current = false;
            setOwnerModuleSelectionInitialized(false);
        }
    }, [reportType]);

    useEffect(() => {
        ownerModuleSelectionDidInitRef.current = false;
        setOwnerModuleSelectionInitialized(false);
    }, [loggedInUser?.company]);

    useEffect(() => {
        if (
            !isOwner ||
            reportType !== ReportType.Reservations ||
            ownerModuleList.length === 0
        ) {
            return;
        }
        if (ownerModuleSelectionDidInitRef.current) return;
        setSelectedOwnerModuleIds(ownerModuleList.map((m: any) => m.id));
        setOwnerModuleSelectionInitialized(true);
        ownerModuleSelectionDidInitRef.current = true;
    }, [isOwner, reportType, ownerModuleList]);

    const groupedOwnerExperiences = useMemo(
        () => buildGroupedExperiences(ownerModuleList),
        [ownerModuleList],
    );

    const { data: completedReservationsReport } = useGetReportQuery(
        `/reports/completed-bookings?period=${reportPeriod}`,
        {
            skip: ![
                ReportType.Monthly,
                ReportType.Quarterly,
                ReportType.YTD,
                ReportType.FY,
            ].includes(reportType),
        },
    );

    const { data: cancelledReservations } = useGetReportQuery(
        `/reports/cancelled-bookings?from=${moment(fromDate).format(
            'YYYY-MM-DD',
        )}&to=${moment(toDate).format('YYYY-MM-DD')}`,
        { skip: reportType !== ReportType.Cancellations },
    );

    const { data: declinedReservations } = useGetReportQuery(
        `/reports/declined-bookings?from=${moment(fromDate).format(
            'YYYY-MM-DD',
        )}&to=${moment(toDate).format('YYYY-MM-DD')}`,
        { skip: reportType !== ReportType.Declined },
    );

    const { data: reservationsReport } = useGetReportQuery(
        `/reports/bookings?from=${moment(fromDate).format(
            'YYYY-MM-DD',
        )}&to=${moment(toDate).format(
            'YYYY-MM-DD',
        )}&status=${status}&moduleType=${moduleTypesForBookingsQuery}&guest=${
            guestId?.value ? guestId?.value : ''
        }&owner=${
            ownerId?.value ? ownerId?.value : ''
        }&visitType=${visitType}&bookerType=${bookerType}`,
        { skip: reportType !== ReportType.Reservations },
    );

    const handleReportPeriod = (value: string) => {
        setReportPeriod(value);
    };

    const handleBookerType = (value: any) => {
        setBookerType(value);
    };
    const handleVisitType = (value: any) => {
        setVisitType(value);
    };

    const handleCalendar1 = (event: React.MouseEvent<HTMLElement>) => {
        setOpenCalendar1(!openCalendar1);
        setAnchorElCalendar1(event.currentTarget);
    };

    const handleCalendar1Close = (event: React.MouseEvent<HTMLElement>) => {
        setOpenCalendar1(!openCalendar1);
        setAnchorElCalendar1(null);
    };

    const handleCalendar2 = (event: React.MouseEvent<HTMLElement>) => {
        setOpenCalendar2(!openCalendar2);
        setAnchorElCalendar2(event.currentTarget);
    };

    const handleCalendar2Close = (event: React.MouseEvent<HTMLElement>) => {
        setOpenCalendar2(!openCalendar2);
        setAnchorElCalendar2(null);
    };

    const { data: getAllReservations, isFetching } = useGetAllReservationsQuery(
        {},
    );

    const theme = useTheme();

    useEffect(() => {
        const companies: any = getAllReservations?.map((val: any) => {
            return val?.guest?.name
                ? { id: val?.guest?.id, name: val?.guest?.name }
                : { id: val?.owner?.id, name: val?.owner?.name };
        });

        const ownerCompanies: any = getAllReservations?.map((val: any) => {
            return val?.owner?.name
                ? { value: val?.owner?.id, label: val?.owner?.name }
                : { value: val?.owner?.id, label: val?.owner?.name };
        });

        const filterOutDuplicatesGuest = companies?.filter(
            (value: any, index: any, self: any) =>
                index ===
                self.findIndex(
                    (t: any) =>
                        t?.name === value?.name && t?.name === value?.name,
                ),
        );

        const filterOutDuplicatesOwner = ownerCompanies?.filter(
            (value: any, index: any, self: any) =>
                index ===
                self.findIndex(
                    (t: any) =>
                        t?.label === value?.label && t?.label === value?.label,
                ),
        );

        const removeCompany = filterOutDuplicatesGuest?.filter(
            (value: any) => !value?.name.includes('BHAG'),
        );

        const companyList = removeCompany?.map((val: any) => {
            return {
                value: val?.id,
                label: val?.name,
            };
        });
        companyList?.splice(0, 0, {
            value: '',
            label: 'All',
        });
        filterOutDuplicatesOwner?.splice(0, 0, {
            value: '',
            label: 'All',
        });

        setGuestCompanyList(companyList);
        setOwnerCompanyList(filterOutDuplicatesOwner);
    }, [getAllReservations]);

    const handleStatus = (value: any) => {
        setStatus(value);
    };

    const handleModuleType = (value: any) => {
        setModuleType(value);
    };
    const handleGuestId = (value: any) => {
        const obj = companyGuestList.find((o: any) => o.value === value);
        setGuestId({ value: value, label: obj.label });
    };

    const handleOwnerId = (value: any) => {
        const obj = companyOwnerList.find((o: any) => o.value === value);
        setOwnerId({ value: value, label: obj.label });
    };

    const options =
        reportType === 'fyReport'
            ? [
                  {
                      value: 'prev-fy',
                      label: 'Previous FY',
                  },
                  {
                      value: 'current-fy',
                      label: 'Current FY',
                  },
              ]
            : reportType === 'monthlyReport'
              ? [
                    {
                        value: 'prev-month',
                        label: 'Previous Month',
                    },
                    {
                        value: 'current-month',
                        label: 'Current Month',
                    },
                ]
              : reportType === 'quarterlyReport'
                ? [
                      {
                          value: 'prev-qrt',
                          label: 'Previous Quarter',
                      },
                      {
                          value: 'current-qrt',
                          label: 'Current Quarter',
                      },
                  ]
                : [];

    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        {
            value: 'approved',
            label: 'Approved',
        },
        {
            value: 'completed',
            label: 'Completed',
        },
        {
            value: 'cancelled',
            label: 'Cancelled',
        },
        {
            value: 'declined',
            label: 'Declined',
        },
    ];

    const moduleTypes = [
        { value: 'boat', label: 'Boat' },
        {
            value: 'property',
            label: 'Property',
        },
        {
            value: 'activity',
            label: 'Activity',
        },
        { value: 'vehicle', label: 'Vehicle' },
        { value: 'commercial', label: 'Commercial' },
    ];

    const reportPeriodLabels: Record<string, string> = {
        'prev-fy': 'Previous FY',
        'current-fy': 'Current FY',
        'prev-month': 'Previous Month',
        'current-month': 'Current Month',
        'prev-qrt': 'Previous Quarter',
        'current-qrt': 'Current Quarter',
    };

    const data = [
        ReportType.Monthly,
        ReportType.Quarterly,
        ReportType.YTD,
        ReportType.FY,
    ].includes(reportType)
        ? completedReservationsReport
        : reportType === ReportType.Cancellations
          ? cancelledReservations
          : reportType === ReportType.Declined
            ? declinedReservations
            : reservationsReport;

    const displayData = useMemo(() => {
        if (
            reportType !== ReportType.Reservations ||
            !isOwner ||
            ownerModuleList.length === 0 ||
            !ownerModuleSelectionInitialized
        ) {
            return data;
        }
        if (!data) return data;
        const allIds = ownerModuleList.map((m: any) => m.id);
        const allSelected =
            selectedOwnerModuleIds.length === allIds.length &&
            allIds.every((id: number) => selectedOwnerModuleIds.includes(id));
        if (allSelected) return data;
        if (selectedOwnerModuleIds.length === 0) return [];
        return data.filter((row: any) => {
            if (row.moduleId != null && row.moduleId !== undefined) {
                return selectedOwnerModuleIds.includes(Number(row.moduleId));
            }
            const mod = ownerModuleList.find(
                (m: any) =>
                    m.name === row.moduleName &&
                    String(m.type).toLowerCase() ===
                        String(row.moduleType).toLowerCase(),
            );
            return mod ? selectedOwnerModuleIds.includes(mod.id) : false;
        });
    }, [
        data,
        reportType,
        isOwner,
        ownerModuleList,
        selectedOwnerModuleIds,
        ownerModuleSelectionInitialized,
    ]);

    const columnVisibility = useMemo(() => {
        return {
            bookerType: isOwner,
            guestName: isOwner,
            status: reportType === ReportType.Reservations,
            cancelledDate: reportType === ReportType.Cancellations,
            cancelledBy: reportType === ReportType.Cancellations,
            cancellationReason: reportType === ReportType.Cancellations,
            declinedDate: reportType === ReportType.Declined,
            declinedBy: reportType === ReportType.Declined,
            declineReason: reportType === ReportType.Declined,
        };
    }, [reportType, isOwner]);

    return (
        <Grid sx={{ Top: isMobile ? '30px' : '0px' }}>
            <style>{getCss(theme)}</style>
            {[
                ReportType.Reservations,
                ReportType.Cancellations,
                ReportType.Declined,
            ].includes(reportType) && (
                <Grid
                    sx={{
                        textAlign: isMobile || isIpad ? 'left' : 'right',
                        display: 'flex',
                        flexDirection: isMobile || isIpad ? 'column' : 'row',
                    }}
                >
                    <Grid
                        sx={{
                            width: '100%',
                            minHeight: 'auto',
                            display: 'flex',
                            flexDirection:
                                isMobile || isIpad ? 'column' : 'row',
                            alignItems: 'left',
                        }}
                    >
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                    paddingBottom: '10px',

                                    justifyContent: 'left',
                                }}
                            >
                                {' '}
                                <Grid
                                    sx={{
                                        width:
                                            isMobile || isIpad ? '49%' : 'auto',
                                        marginRight:
                                            isMobile || isIpad ? '0px' : '15px',
                                        // backgroundColor: 'green',
                                        display: 'flex',
                                        justifyContent: 'left',
                                    }}
                                >
                                    <CustomTextField
                                        color='dark'
                                        label='From'
                                        value={moment(fromDate)?.format(
                                            'DD/MM/YYYY',
                                        )}
                                        onClick={handleCalendar1}
                                        fullWidth={isMobile || isIpad}
                                        InputProps={{
                                            style: {
                                                minHeight: '30px',
                                                maxHeight: '33px',
                                            },
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        disableRipple
                                                        onClick={
                                                            handleCalendar1
                                                        }
                                                    >
                                                        <DateRangeIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Popover
                                        open={openCalendar1}
                                        anchorEl={anchorElCalendar1}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        onClose={handleCalendar1Close}
                                    >
                                        <Calendar
                                            onChange={(item) => [
                                                setFromDate(item),
                                            ]}
                                            date={fromDate}
                                            weekStartsOn={1}
                                            rangeColors={[
                                                `${theme.palette.textAccent.main}`,
                                            ]}
                                            color={`${theme.palette.textAccent.main}`}
                                        />
                                    </Popover>
                                </Grid>
                                <Grid
                                    sx={{
                                        width:
                                            isMobile || isIpad ? '50%' : 'auto',
                                        marginLeft: isIpad ? '10px' : '0px',
                                        display: 'flex',
                                        justifyContent: 'left',
                                        paddingLeft:
                                            isMobile || isIpad ? '5px' : '0px',
                                    }}
                                >
                                    <CustomTextField
                                        label='To'
                                        color='dark'
                                        fullWidth={isMobile || isIpad}
                                        value={moment(toDate)?.format(
                                            'DD/MM/YYYY',
                                        )}
                                        onClick={handleCalendar2}
                                        InputProps={{
                                            style: {
                                                minHeight: '30px',
                                                maxHeight: '33px',
                                            },
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        disableRipple
                                                        onClick={
                                                            handleCalendar2
                                                        }
                                                    >
                                                        <DateRangeIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Popover
                                        open={openCalendar2}
                                        anchorEl={anchorElCalendar2}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        onClose={handleCalendar2Close}
                                    >
                                        <Calendar
                                            onChange={(item) => [
                                                setToDate(item),
                                            ]}
                                            date={toDate}
                                            weekStartsOn={1}
                                            rangeColors={[
                                                `${theme.palette.textAccent.main}`,
                                            ]}
                                            color={`${theme.palette.textAccent.main}`}
                                        />
                                    </Popover>
                                </Grid>
                            </Grid>
                            {reportType === ReportType.Reservations && (
                                <Grid
                                    container
                                    spacing={0}
                                    columns={1}
                                    sx={{
                                        display: 'flex',
                                        flexDirection:
                                            isMobile || isIpad
                                                ? 'column'
                                                : 'row',
                                        width: '100%',
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            width:
                                                isMobile || isIpad
                                                    ? '100%'
                                                    : 'auto',
                                            marginRight:
                                                isMobile || isIpad
                                                    ? '0px'
                                                    : '15px',
                                            marginTop:
                                                isMobile || isIpad
                                                    ? '10px'
                                                    : '0px',
                                        }}
                                    >
                                        {' '}
                                        <MultiSelectDropdownFilter
                                            id={'status'}
                                            options={statusOptions}
                                            handleChange={handleStatus}
                                            label={'Status:'}
                                            selectedValue={status}
                                            backgroundColor={
                                                theme.palette.neutral.main
                                            }
                                            border={theme.palette.midGrey.main}
                                            minHeight={'30px'}
                                            maxHeight={'33px'}
                                        />
                                    </Grid>
                                    <Grid
                                        sx={{
                                            width:
                                                isMobile || isIpad
                                                    ? '100%'
                                                    : 'auto',
                                            marginRight:
                                                isMobile || isIpad
                                                    ? '0px'
                                                    : '15px',
                                            marginTop:
                                                isMobile || isIpad
                                                    ? '10px'
                                                    : '0px',
                                        }}
                                    >
                                        {' '}
                                        {isOwner ? (
                                            <GroupedExperienceTypeFilter
                                                id={'type-experiences'}
                                                groups={groupedOwnerExperiences}
                                                selectedModuleIds={
                                                    selectedOwnerModuleIds
                                                }
                                                onChange={
                                                    setSelectedOwnerModuleIds
                                                }
                                                label='Experience:'
                                                backgroundColor={
                                                    theme.palette.neutral.main
                                                }
                                                border={
                                                    theme.palette.midGrey.main
                                                }
                                                minHeight={'30px'}
                                                maxHeight={'33px'}
                                            />
                                        ) : (
                                            <MultiSelectDropdownFilter
                                                id={'type'}
                                                options={moduleTypes}
                                                handleChange={handleModuleType}
                                                label='Type:'
                                                selectedValue={moduleType}
                                                backgroundColor={
                                                    theme.palette.neutral.main
                                                }
                                                border={
                                                    theme.palette.midGrey.main
                                                }
                                                minHeight={'30px'}
                                                maxHeight={'33px'}
                                            />
                                        )}
                                    </Grid>
                                    {loggedInUser?.role.includes(
                                        'guest',
                                    ) ? null : (
                                        <Grid
                                            sx={{
                                                width:
                                                    isMobile || isIpad
                                                        ? '100%'
                                                        : 'auto',
                                                marginRight:
                                                    isMobile || isIpad
                                                        ? '0px'
                                                        : '15px',
                                                marginTop:
                                                    isMobile || isIpad
                                                        ? '10px'
                                                        : '0px',
                                            }}
                                        >
                                            {' '}
                                            <DropdownFilter
                                                id={'guest'}
                                                options={
                                                    companyGuestList !==
                                                    undefined
                                                        ? companyGuestList
                                                        : []
                                                }
                                                handleChange={handleGuestId}
                                                selectedValue={
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                                marginRight:
                                                                    '5px',
                                                            }}
                                                        >
                                                            Guest:
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                            }}
                                                        >
                                                            {guestId?.label}
                                                        </Typography>
                                                    </Grid>
                                                }
                                                backgroundColor={
                                                    theme.palette.neutral.main
                                                }
                                                border={
                                                    theme.palette.midGrey.main
                                                }
                                                minHeight={'30px'}
                                                maxHeight={'33px'}
                                            />
                                        </Grid>
                                    )}
                                    {loggedInUser?.role.includes ===
                                    'admin' ? null : (
                                        <Grid
                                            sx={{
                                                width:
                                                    isMobile || isIpad
                                                        ? '100%'
                                                        : 'auto',
                                                marginRight:
                                                    isMobile || isIpad
                                                        ? '0px'
                                                        : '15px',
                                                marginTop:
                                                    isMobile || isIpad
                                                        ? '10px'
                                                        : '0px',
                                            }}
                                        >
                                            {' '}
                                            <DropdownFilter
                                                id={'ownerList'}
                                                options={
                                                    companyOwnerList !==
                                                    undefined
                                                        ? companyOwnerList
                                                        : []
                                                }
                                                handleChange={handleOwnerId}
                                                selectedValue={
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                                marginRight:
                                                                    '5px',
                                                            }}
                                                        >
                                                            Owner:
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                            }}
                                                        >
                                                            {ownerId?.label}
                                                        </Typography>
                                                    </Grid>
                                                }
                                                backgroundColor={
                                                    theme.palette.neutral.main
                                                }
                                                border={
                                                    theme.palette.midGrey.main
                                                }
                                                minHeight={'30px'}
                                                maxHeight={'33px'}
                                            />
                                        </Grid>
                                    )}

                                    <Grid
                                        sx={{
                                            width:
                                                isMobile || isIpad
                                                    ? '100%'
                                                    : 'auto',
                                            marginRight:
                                                isMobile || isIpad
                                                    ? '0px'
                                                    : '15px',
                                            marginTop:
                                                isMobile || isIpad
                                                    ? '10px'
                                                    : '0px',
                                        }}
                                    >
                                        {' '}
                                        <DropdownFilter
                                            id={'visitType'}
                                            options={[
                                                // { value: 'All', label: 'All' },
                                                { value: '', label: 'All' },
                                                {
                                                    value: 'business',
                                                    label: 'Business',
                                                },
                                                {
                                                    value: 'personal',
                                                    label: 'Personal',
                                                },
                                            ]}
                                            handleChange={handleVisitType}
                                            selectedValue={
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '16px',
                                                            marginRight: '5px',
                                                        }}
                                                    >
                                                        Visit Type:
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        {visitType === undefined
                                                            ? 'All'
                                                            : visitType}
                                                    </Typography>
                                                </Grid>
                                            }
                                            backgroundColor={
                                                theme.palette.neutral.main
                                            }
                                            border={theme.palette.midGrey.main}
                                            minHeight={'30px'}
                                            maxHeight={'33px'}
                                        />
                                    </Grid>

                                    {loggedInUser?.role.includes(
                                        'guest',
                                    ) ? null : (
                                        <Grid
                                            sx={{
                                                width:
                                                    isMobile || isIpad
                                                        ? '100%'
                                                        : 'auto',
                                                marginRight:
                                                    isMobile || isIpad
                                                        ? '0px'
                                                        : '15px',
                                                marginTop:
                                                    isMobile || isIpad
                                                        ? '10px'
                                                        : '0px',
                                            }}
                                        >
                                            {' '}
                                            <DropdownFilter
                                                id={'bookerType'}
                                                options={[
                                                    { value: '', label: 'All' },
                                                    {
                                                        value: 'owner',
                                                        label: 'Owner',
                                                    },
                                                    {
                                                        value: 'guest',
                                                        label: 'Guest',
                                                    },
                                                ]}
                                                handleChange={handleBookerType}
                                                selectedValue={
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                                marginRight:
                                                                    '5px',
                                                            }}
                                                        >
                                                            Reserved By:
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '16px',
                                                            }}
                                                        >
                                                            {bookerType ===
                                                            undefined
                                                                ? 'All'
                                                                : bookerType}
                                                        </Typography>
                                                    </Grid>
                                                }
                                                backgroundColor={
                                                    theme.palette.neutral.main
                                                }
                                                border={
                                                    theme.palette.midGrey.main
                                                }
                                                minHeight={'30px'}
                                                maxHeight={'33px'}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        sx={{
                            paddingBottom: 1,
                        }}
                    >
                        {showGraphView ? (
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setShowGraphView(!showGraphView)}
                            >
                                <FormatListBulletedIcon fontSize='small' />
                            </CustomButton>
                        ) : (
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setShowGraphView(!showGraphView)}
                            >
                                <EqualizerIcon fontSize='small' />
                            </CustomButton>
                        )}
                    </Grid>
                </Grid>
            )}
            {[
                ReportType.Monthly,
                ReportType.Quarterly,
                ReportType.FY,
                ReportType.YTD,
            ].includes(reportType) && (
                <Grid
                    sx={{
                        display: 'flex',
                        gap: 1,
                    }}
                >
                    <Grid
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'left',
                        }}
                    >
                        {[
                            ReportType.Monthly,
                            ReportType.Quarterly,
                            ReportType.FY,
                        ].includes(reportType) && (
                            <Grid
                                sx={{
                                    width: 'auto',
                                    marginRight: isMobile ? '0px' : '15px',
                                }}
                            >
                                <DropdownFilter
                                    id={reportType}
                                    options={options}
                                    handleChange={handleReportPeriod}
                                    selectedValue={
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '16px',
                                                    marginRight: '5px',
                                                }}
                                            >
                                                Reporting period:
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            >
                                                {reportPeriodLabels[
                                                    reportPeriod
                                                ] || 'Unknown period'}
                                            </Typography>
                                        </Grid>
                                    }
                                    backgroundColor={theme.palette.neutral.main}
                                    border={theme.palette.midGrey.main}
                                    minHeight={'30px'}
                                    maxHeight={'33px'}
                                />
                            </Grid>
                        )}
                    </Grid>

                    <Grid
                        sx={{
                            paddingBottom: 1,
                        }}
                    >
                        {showGraphView ? (
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setShowGraphView(!showGraphView)}
                            >
                                <FormatListBulletedIcon fontSize='small' />
                            </CustomButton>
                        ) : (
                            <CustomButton
                                disableRipple
                                variant='outlined'
                                color='primary'
                                onClick={() => setShowGraphView(!showGraphView)}
                            >
                                <EqualizerIcon fontSize='small' />
                            </CustomButton>
                        )}
                    </Grid>
                </Grid>
            )}
            <Grid sx={{ paddingBottom: 2 }}>
                {showGraphView ? (
                    <ReportGraph data={displayData} />
                ) : (
                    <ReportList
                        data={displayData}
                        columnVisibility={columnVisibility}
                        reportType={reportType}
                    />
                )}
            </Grid>
        </Grid>
    );
}
